import * as React from 'react';
import { ArrowDownRight, ArrowUpRight, Info, Minus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  statCardDeltaRecipe,
  statCardRecipe,
} from '../recipes/stat-card.recipe';
import { Button } from './Button';
import { Card, CardBody, type CardProps } from './Card';
import { Popover, PopoverContent, PopoverTrigger } from './DatePicker';
import { Icon } from './Icon';
import { Inline } from './Layout';
import { Text } from './Text';

export interface StatCardDelta {
  /** Which way the arrow points. */
  direction: 'up' | 'down' | 'flat';
  /** What to print beside the arrow — "18%", "4.0 pts", "new". */
  label?: React.ReactNode;
  /**
   * Whether this movement is good news, which is what picks the colour.
   * `null` (or omitted) is a movement with no reading — no change, or
   * nothing to compare with — and stays muted.
   */
  good?: boolean | null;
}

export interface StatCardProps extends Omit<CardProps, 'title'> {
  /** A glyph beside the label. */
  icon?: LucideIcon;
  label: React.ReactNode;
  /** Already formatted — the card does no arithmetic. */
  value: React.ReactNode;
  delta?: StatCardDelta;
  /** What the delta is measured against — "vs the 7 days before". */
  comparison?: React.ReactNode;
  /**
   * What this number counts, behind an info button. The button is named
   * "What <label> means" for a screen reader; pass `definitionLabel` when
   * the label is not plain text.
   */
  definition?: React.ReactNode;
  definitionLabel?: string;
}

const DIRECTION_ICON = {
  up: ArrowUpRight,
  down: ArrowDownRight,
  flat: Minus,
} as const;

const trendOf = (delta: StatCardDelta) =>
  delta.good === null || delta.good === undefined
    ? 'neutral'
    : delta.good
      ? 'good'
      : 'bad';

const TREND_TONE = {
  good: 'success',
  bad: 'destructive',
  neutral: 'muted',
} as const;

/**
 * One headline number.
 *
 *   <StatCard icon={Users} label="Visitors" value="1,284"
 *     delta={{ direction: 'up', label: '18%', good: true }}
 *     comparison="vs the 7 days before"
 *     definition="People who opened at least one page." />
 *
 * The delta's colour follows `good`, not `direction`: a bounce rate falling
 * is green. The label wraps rather than truncates — two tiles across a phone
 * is not enough for "Avg time on site", and "Avg time o…" is a worse answer
 * than a second line.
 */
export const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
  function StatCard(
    {
      icon,
      label,
      value,
      delta,
      comparison,
      definition,
      definitionLabel,
      className,
      padding = 'md',
      ...props
    },
    ref
  ) {
    const DeltaIcon = delta ? DIRECTION_ICON[delta.direction] : null;
    const trend = delta ? trendOf(delta) : 'neutral';
    const buttonLabel =
      definitionLabel ??
      (typeof label === 'string'
        ? `What ${label.toLowerCase()} means`
        : 'What this means');

    return (
      <Card ref={ref} padding={padding} className={className} {...props}>
        <CardBody className={statCardRecipe()}>
          <Inline gap={2} align="center" justify="between">
            <Inline gap={2} align="start" grow>
              {icon ? (
                <Icon icon={icon} size="sm" tone="muted" className="mt-0.5" />
              ) : null}
              <Text variant="body-sm" tone="muted">
                {label}
              </Text>
            </Inline>
            {definition ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    className="-mr-1 text-muted-foreground"
                    aria-label={buttonLabel}
                  >
                    <Info className="size-icon-sm" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="max-w-[16rem]">
                  <Text variant="body-sm" tone="muted">
                    {definition}
                  </Text>
                </PopoverContent>
              </Popover>
            ) : null}
          </Inline>

          <Text variant="h2" numeric>
            {value}
          </Text>

          {delta && DeltaIcon ? (
            <Inline
              gap={1}
              align="center"
              wrap
              className={statCardDeltaRecipe({ trend })}
            >
              <Icon icon={DeltaIcon} size="xs" tone={TREND_TONE[trend]} />
              <Text variant="caption-strong" numeric tone={TREND_TONE[trend]}>
                {delta.label || '—'}
              </Text>
              {comparison ? (
                <Text variant="caption" tone="muted" truncate>
                  {comparison}
                </Text>
              ) : null}
            </Inline>
          ) : comparison ? (
            <Text variant="caption" tone="muted">
              {comparison}
            </Text>
          ) : null}
        </CardBody>
      </Card>
    );
  }
);
